import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Checkbox,
    Collapse,
    FormControlLabel,
    Stack,
    TextField,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useSearchParams } from "react-router-dom"

import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import ApplicationsFilterPanelFrame from "../../components/fitlerPanel/applicationsFilterPanelFrame"
import AppControlledDateRange from "../../components/inputs/controlled/appControlledDateRange"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import AppPaper from "../../components/surfaces/appPaper"
import InstallerApplicationTable from "../../components/tables/applicationTable/installerApplicationTable"
import { InstallerApplicationStatuses } from "../../constants/enums"
import {
    APPLICATION_ID_KW,
    INSTALLER_DATE_FROM_KW,
    INSTALLER_DATE_TO_KW,
    READY_FOR_CX,
    SLIDE_IN_TIMEOUT,
    STATUS,
} from "../../constants/generalConstants"
import {
    TT_HELP_INSTALLER_ADMIN_LIST_APPLICATIONS,
    TT_HELP_INSTALLER_LIST_APPLICATIONS,
} from "../../constants/tooltips"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import { getShowDnoApplicationFilters } from "../../redux/preferencesSlice"
import useGetInstallerApplications from "../../services/middleware/hooks/installerInterface/useGetInstallerApplications"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

const InstallerListApplications = (): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        data,
        isError,
        isValidating,
        isLoading,
        isFinalPage,
        page,
        setPage,
    } = useGetInstallerApplications(searchParams)

    const showFilters = useGetState(getShowDnoApplicationFilters)

    // Filter and sort hooks
    const {
        applicationId,
        setFilterFromTextfield,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        status,
        readyForCx,
        setOrderBy,
    } = useApplicationDeriveFilters(searchParams, setSearchParams, false)
    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localApplicationId, setLocalApplicationId] =
        useState<string>(applicationId)

    const { isInstallerAdmin } = useGetUserData()

    return (
        <Grid container spacing={GRID_SPACING}>
            <ApplicationsFilterPanelFrame
                applicationId={applicationId}
                isValidating={isValidating}
                all={!!isInstallerAdmin}
                applications={
                    isInstallerAdmin
                        ? "Applications Admin View"
                        : "My Applications"
                }
                tooltip={
                    isInstallerAdmin
                        ? TT_HELP_INSTALLER_ADMIN_LIST_APPLICATIONS
                        : TT_HELP_INSTALLER_LIST_APPLICATIONS
                }
                isDnoMode={false}
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING + 1}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={3} sm={6} xs={12}>
                                <TextField
                                    label="Application ID"
                                    value={localApplicationId}
                                    placeholder="Full application ID required..."
                                    onChange={(event) => {
                                        setLocalApplicationId(
                                            event.target.value
                                        )
                                        setFilterFromTextfield(
                                            event.target.value,
                                            APPLICATION_ID_KW
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledDateRange
                                    label="Date from"
                                    dateValue={dateFrom}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            INSTALLER_DATE_FROM_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledDateRange
                                    label="Date to"
                                    dateValue={dateTo}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            INSTALLER_DATE_TO_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledSelect
                                    value={status}
                                    options={Object.values(
                                        InstallerApplicationStatuses
                                    )}
                                    label="Status"
                                    setValue={(
                                        e_: SyntheticEvent<Element, Event>,
                                        nv: string | number | null
                                    ) =>
                                        setFilterFromTextfield(
                                            nv as string,
                                            STATUS
                                        )
                                    }
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={readyForCx}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    READY_FOR_CX
                                                )
                                            }}
                                        />
                                    }
                                    label="Ready for Commissioning Only"
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            <InstallerApplicationTable
                                searchResults={data}
                                orderBy={orderBy}
                                setOrderBy={setOrderBy}
                                orderDirection={orderDirection}
                                isLoading={isLoading}
                            />
                            {data && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {!isFinalPage && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isValidating}
                                            loadingPosition="end"
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: "40%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default InstallerListApplications
